import React, { useEffect, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { Trans } from 'react-i18next'

import { Button } from '@fullfabric/alma-mater'

import {
  getCountries as apiGetCountries,
  getPolicy as apiGetPolicy,
  getCascadingDropDownOptions
} from '@fullfabric/public-api'

import { useCurrentUser } from '@fullfabric/authorization-officer'
import Altcha from './Form/Altcha'
import {
  FormAvailabilityAlert,
  SchemableForm,
  isFormOpenForSubmissions,
  triggerSubmitFormEvent
} from '@fullfabric/alma-mater/schemable-forms'

import FormSubmissionSuccessMessage from './Form/FormSubmissionSuccessMessage'
import useHandleSubmit from './Form/useHandleSubmit'
import useFormQuery from './Form/useFormQuery'

import { useQueryOpts } from '../utils/queryOpts'

import '@fullfabric/alma-mater/dist/index.css'

import styles from './styles.module.scss'
import '../i18n'

export default function Form({
  formId,
  preFillData,
  onBeforeFormStarts,
  onFormSubmitted,
  locale
}) {
  const user = useCurrentUser()
  const queryOpts = useQueryOpts()
  const [altchaPayload, setAltchaPayload] = useState(null)

  const {
    form,
    loading: isFormLoading,
    errored: isFormErrored
  } = useFormQuery(formId, queryOpts)

  const { data: countriesAsOptions } = useQuery(
    ['get-countries'],
    async () => await apiGetCountries(queryOpts)
  )

  const { data: privacyPolicy } = useQuery(
    ['get-policy', 'privacy_policy'],
    async () => await apiGetPolicy('privacy_policy', queryOpts)
  )

  const showMarketingConsent = form?.show_marketing_consent !== false

  const { data: marketingPolicy } = useQuery(
    ['get-policy', 'marketing_policy'],
    async () => await apiGetPolicy('marketing_policy', queryOpts),
    { enabled: showMarketingConsent }
  )

  const schemableFormRef = useRef()

  const isOpen = isFormOpenForSubmissions(
    form?.start_submissions,
    form?.end_submissions
  )

  const [{ isSubmitted, submissionError, isSubmitting }, setSubmissionState] =
    useState({
      isSubmitted: false,
      submissionError: null,
      isSubmitting: false
    })

  const handleSubmit = useHandleSubmit({
    formId,
    redirectTo: form?.redirect_path,
    externalDomainToken: form?.external_domain_token,
    onStartSubmit: () => {
      setSubmissionState((state) => ({
        ...state,
        isSubmitting: true,
        submissionError: null
      }))
    },
    onSubmitted: () => {
      setSubmissionState({
        isSubmitted: true,
        submissionError: null,
        isSubmitting: false
      })
      if (onFormSubmitted) onFormSubmitted()
    },
    onNotSubmitted: (error) =>
      setSubmissionState((state) => ({
        ...state,
        isSubmitting: false,
        submissionError: error
      })),
    altchaPayload
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onBeforeFormStarts && onBeforeFormStarts(), [])

  const loadCascadingDropDownOptionsClosure =
    (queryOpts) => async (field, parentValue) =>
      getCascadingDropDownOptions(field, parentValue, queryOpts)

  const loadCascadingDropDownOptions =
    loadCascadingDropDownOptionsClosure(queryOpts)

  if (isFormErrored) {
    return (
      <div className="embeddable-form-error">
        Failed to load form. Please double check the form identifier and
        external domain settings.
      </div>
    )
  }

  if (!isFormLoading && !isOpen) {
    return (
      <FormAvailabilityAlert
        openDate={form?.start_submissions}
        closeDate={form?.end_submissions}
        userTimezone={user?.timezoneName}
        userLocale={locale}
      />
    )
  }

  if (isSubmitted) {
    return (
      <FormSubmissionSuccessMessage successMessage={form?.success_message} />
    )
  }

  const antiSpamCaptchaEnabled = !!form?.anti_spam_captcha
  const challengeUrl = `${queryOpts.baseUrl}/api/forms/${formId}/altcha_challenge?external_domain_token=${form?.external_domain_token}`

  return (
    <>
      {!isFormLoading && form?.end_submissions && (
        <div className={styles.alertContainer}>
          <FormAvailabilityAlert
            openDate={form?.start_submissions}
            closeDate={form?.end_submissions}
            userTimezone={user?.timezoneName}
            userLocale={locale}
          />
        </div>
      )}

      <SchemableForm
        formRef={schemableFormRef}
        countriesAsOptions={countriesAsOptions || []}
        data={preFillData}
        error={submissionError}
        errorDetailScope="forms/submission"
        handleSubmit={handleSubmit}
        isLoading={isFormLoading}
        locale={locale}
        marketingPolicy={showMarketingConsent ? marketingPolicy : undefined}
        privacyPolicy={privacyPolicy}
        sectionClassName="schemable-form-section"
        separatorClassName="schemable-form-separator"
        schema={form?.schema}
        loadCascadingDropDownOptions={loadCascadingDropDownOptions}
        user={user}
        hideSubmitButton
      />

      {antiSpamCaptchaEnabled && (
        <div className={styles.altchaWidget}>
          <Altcha
            setAltchaPayload={setAltchaPayload}
            locale={locale}
            challengeUrl={challengeUrl}
            error={submissionError?.altcha}
          />
        </div>
      )}

      <Button
        primary
        onClick={() => triggerSubmitFormEvent(schemableFormRef)}
        loading={isSubmitting}
        className={styles.submitButton}
        disabled={!altchaPayload && antiSpamCaptchaEnabled}
      >
        <Trans>Submit</Trans>
      </Button>
    </>
  )
}
